import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { useCampus } from '../lib/useCampus';
import { api, ApiError, asList } from '../lib/api';
import { tripsApi, type Trip, type TripConsent } from '../lib/tripsApi';

interface ClassLite { id: string; label: string; }
const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
function currentAY() { const n = new Date(); return n.getMonth() >= 5 ? `${n.getFullYear()}-${n.getFullYear() + 1}` : `${n.getFullYear() - 1}-${n.getFullYear()}`; }

export function TripsPage() {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [instId, setInstId] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassLite[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => { tripsApi.list().then((r) => setTrips(asList(r))).catch(() => setTrips([])); }, []);
  useEffect(load, [load]);
  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);
  useEffect(() => {
    if (instId) api.get<ClassLite[]>(`/api/classes?institutionId=${instId}`).then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, [instId]);

  const act = async (fn: () => Promise<unknown>) => {
    setMsg(null);
    try { await fn(); load(); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Action failed.'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Student life</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>School trips</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Excursions and educational visits. A child travels only when their guardian has actively said
          yes — no reply is never taken as permission.
        </p>
      </div>

      {msg && <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13, color: '#c1272d' }}>{msg}</div>}

      <button className="btn btn-accent" style={{ marginBottom: 16 }} onClick={() => setCreating(!creating)}>
        {creating ? 'Cancel' : '+ New trip'}
      </button>

      {creating && <NewTrip instId={instId} onCreated={() => { setCreating(false); load(); }} />}

      {trips.length === 0 && !creating
        ? <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>No trips yet.</div>
        : trips.map((t) => (
          <div key={t.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{t.title}</span>
                {t.destination && <span style={{ color: 'var(--ink-3)', fontSize: 13 }}> · {t.destination}</span>}
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  {formatDate(t.departDate)} → {formatDate(t.returnDate)}
                  {' · '}consent closes {formatDate(t.consentDeadline)}
                  {t.costPaise > 0 ? ` · ${rupees(t.costPaise)}` : ' · free'}
                </div>
              </div>
              <span className="pill" style={{ fontSize: 11 }}>{t.stage}</span>
            </div>

            <div style={{ display: 'flex', gap: 16, margin: '12px 0', fontSize: 13, flexWrap: 'wrap' }}>
              <span><strong style={{ color: '#1a7f37' }}>{t.granted}</strong> going</span>
              <span><strong>{t.pending}</strong> awaiting reply</span>
              <span><strong style={{ color: '#c1272d' }}>{t.declined}</strong> declined</span>
              {t.capacity > 0 && <span style={{ color: t.seatsLeft === 0 ? '#c1272d' : 'var(--ink-2)' }}>
                {t.seatsLeft} of {t.capacity} seats left</span>}
              {t.leadStaffName && <span style={{ color: 'var(--ink-3)' }}>Lead: {t.leadStaffName}</span>}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {t.stage === 'Draft' && <LeadAssign trip={t} onDone={load} />}
              {t.stage === 'Draft' && <button className="btn btn-accent" onClick={() => act(() => tripsApi.open(t.id))}>Open for consent</button>}
              {t.stage === 'Open' && <InviteClass trip={t} classes={classes} onDone={load} />}
              {t.stage === 'Open' && <button className="btn" onClick={() => act(() => tripsApi.close(t.id))}>Close consent</button>}
              {t.stage === 'Closed' && t.costPaise > 0 &&
                <button className="btn btn-accent" onClick={() => act(async () => {
                  const r = await tripsApi.charge(t.id, currentAY());
                  setMsg(`Charged ${r.charged} student(s).${r.skipped ? ` ${r.skipped} skipped (already charged).` : ''}`);
                })}>Charge {t.granted} going</button>}
              {t.stage === 'Closed' && <button className="btn" onClick={() => act(() => tripsApi.complete(t.id))}>Mark completed</button>}
              <button className="btn" onClick={() => setOpen(open === t.id ? null : t.id)}>
                {open === t.id ? 'Hide' : 'Roster & replies'}
              </button>
              {t.stage !== 'Cancelled' && t.stage !== 'Completed' &&
                <button className="btn" onClick={() => { if (window.confirm('Cancel this trip?')) act(() => tripsApi.cancel(t.id)); }}>Cancel</button>}
            </div>

            {open === t.id && <Roster trip={t} />}
          </div>
        ))}
    </div>
  );
}

function NewTrip({ instId, onCreated }: { instId: string | null; onCreated: () => void }) {
  const [f, setF] = useState({ title: '', destination: '', departDate: '', returnDate: '', consentDeadline: '', cost: '', capacity: '' });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const create = async () => {
    if (!instId) return;
    setErr(null);
    try {
      await tripsApi.create({
        institutionId: instId, title: f.title.trim(), destination: f.destination.trim() || null,
        description: null, departDate: f.departDate, returnDate: f.returnDate || f.departDate,
        consentDeadline: f.consentDeadline || f.departDate,
        costPaise: Math.round(Number(f.cost) * 100) || 0, capacity: Number(f.capacity) || 0,
        eligibleClassIds: null,
      });
      onCreated();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not create the trip.'); }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <input className="input" placeholder="Trip title" value={f.title} onChange={set('title')} style={{ flex: '2 1 200px' }} />
        <input className="input" placeholder="Destination" value={f.destination} onChange={set('destination')} style={{ flex: '1 1 160px' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div><label className="label">Departs</label><input className="input" type="date" value={f.departDate} onChange={set('departDate')} /></div>
        <div><label className="label">Returns</label><input className="input" type="date" value={f.returnDate} onChange={set('returnDate')} /></div>
        <div><label className="label">Consent closes</label><input className="input" type="date" value={f.consentDeadline} onChange={set('consentDeadline')} /></div>
        <div><label className="label">Cost ₹ (0 = free)</label><input className="input" type="number" value={f.cost} onChange={set('cost')} style={{ width: 120 }} /></div>
        <div><label className="label">Seats (0 = no cap)</label><input className="input" type="number" value={f.capacity} onChange={set('capacity')} style={{ width: 120 }} /></div>
      </div>
      {err && <div style={{ color: '#c1272d', fontSize: 13, marginBottom: 8 }}>{err}</div>}
      <button className="btn btn-accent" disabled={!instId || !f.title.trim() || !f.departDate} onClick={create}>Create trip</button>
    </div>
  );
}

function LeadAssign({ trip, onDone }: { trip: Trip; onDone: () => void }) {
  const assign = async () => {
    const name = window.prompt('Staff member in charge (name):'); if (!name) return;
    const phone = window.prompt('Emergency contact number for the day:') || null;
    try {
      const me = await api.get<{ id: string }>('/api/auth/me');
      await tripsApi.assignLead(trip.id, { userId: me.id, name, emergencyContact: phone });
      onDone();
    } catch { /* surfaced by the page */ }
  };
  return <button className="btn" onClick={assign}>{trip.leadStaffName ? 'Change lead' : 'Assign lead'}</button>;
}

function InviteClass({ trip, classes, onDone }: { trip: Trip; classes: ClassLite[]; onDone: () => void }) {
  const [cls, setCls] = useState('');
  const invite = async () => {
    if (!cls) return;
    await tripsApi.inviteClass(trip.id, cls);
    setCls(''); onDone();
  };
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <select className="input" value={cls} onChange={(e) => setCls(e.target.value)} style={{ width: 'auto' }}>
        <option value="">Invite a class…</option>
        {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <button className="btn" disabled={!cls} onClick={invite}>Invite</button>
    </span>
  );
}

function Roster({ trip }: { trip: Trip }) {
  const [rows, setRows] = useState<TripConsent[]>([]);
  const [view, setView] = useState<'going' | 'all'>('going');
  const load = useCallback(() => {
    (view === 'going' ? tripsApi.roster(trip.id) : tripsApi.consents(trip.id)).then((r) => setRows(asList(r))).catch(() => setRows([]));
  }, [trip.id, view]);
  useEffect(load, [load]);

  const colour = (s: string) => s === 'Granted' ? '#1a7f37' : s === 'Declined' ? '#c1272d' : 'var(--ink-3)';

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button className={view === 'going' ? 'btn btn-accent' : 'btn'} style={{ padding: '3px 10px' }} onClick={() => setView('going')}>
          Roster ({trip.granted})
        </button>
        <button className={view === 'all' ? 'btn btn-accent' : 'btn'} style={{ padding: '3px 10px' }} onClick={() => setView('all')}>
          All replies
        </button>
      </div>
      {rows.length === 0 ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Nobody yet.</div>
        : rows.map((c) => (
          <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '5px 0', flexWrap: 'wrap' }}>
            <span style={{ minWidth: 160, fontWeight: 500 }}>{c.studentName}</span>
            <span style={{ color: 'var(--ink-3)', minWidth: 50 }}>{c.classLabel}</span>
            <span style={{ color: colour(c.state), minWidth: 70 }}>{c.state}</span>
            {c.emergencyPhone && <span style={{ color: 'var(--ink-2)' }}>☎ {c.emergencyPhone}</span>}
            {c.medicalNote && <span className="pill" style={{ fontSize: 11, background: '#fdf0e0', color: '#b26a00' }}>⚕ {c.medicalNote}</span>}
            {c.invoiceId && <span style={{ fontSize: 11, color: '#1a7f37' }}>charged</span>}
            {view === 'going' && trip.stage === 'Closed' &&
              <button className="btn" style={{ padding: '1px 8px', fontSize: 11 }}
                onClick={async () => { await tripsApi.markAttended(trip.id, c.id, !c.attended); load(); }}>
                {c.attended ? '✓ boarded' : 'mark boarded'}
              </button>}
          </div>
        ))}
    </div>
  );
}
